import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtMoney, fmtNumber, fmtDate, vehicleLabel } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import {
  CarSimple,
  Motorcycle,
  Truck,
  CurrencyDollar,
  TrendUp,
  Receipt,
  Package,
  Users,
  CalendarCheck,
  Bank,
  ArrowsLeftRight,
  WarningCircle,
  Clock,
  ClipboardText,
} from "@phosphor-icons/react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";
import { CHART_TOOLTIP_STYLE, CHART_DOT_STYLE } from "@/lib/constants";
import FollowUpsWidget from "@/components/FollowUpsWidget";
import PackageUptakeWidget from "@/components/PackageUptakeWidget";
import PendingApprovalsWidget from "@/components/PendingApprovalsWidget";
import LifecycleWidget from "@/components/LifecycleWidget";
import BranchFilter from "@/components/BranchFilter";
import ModelCategoryFilter from "@/components/ModelCategoryFilter";
import SalesMixDonut from "@/components/SalesMixDonut";

const TYPE_META = [
  { key: "car", label: "Cars", icon: CarSimple },
  { key: "bike", label: "Bikes", icon: Motorcycle },
  { key: "truck", label: "Trucks", icon: Truck },
];

function Stat({ label, value, icon: Icon, accent, testId }) {
  return (
    <div
      data-testid={testId}
      className="bg-canvas-card border border-canvas-line p-5 flex flex-col gap-3 hover:border-ink transition-colors duration-150"
    >
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-ink-muted">
          {label}
        </span>
        <div className={`w-7 h-7 flex items-center justify-center ${accent || "bg-ink text-white"}`}>
          <Icon size={14} weight="bold" />
        </div>
      </div>
      <div className="font-display text-3xl font-bold tracking-tight text-ink">
        {value}
      </div>
    </div>
  );
}

function RevenueChart({ monthly, modelFilter }) {
  const formatRevenue = (v, name) => (name === "revenue" ? fmtMoney(v) : v);
  return (
    <div className="lg:col-span-2 bg-canvas-card border border-canvas-line p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-ink-muted mb-1">
            01 / Revenue Trend
          </div>
          <h3 className="font-display text-lg font-semibold text-ink">
            Last 6 months{modelFilter ? ` · ${modelFilter}` : ""}
          </h3>
        </div>
      </div>
      <div className="h-64" style={{ minHeight: 240 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={monthly}>
            <CartesianGrid stroke="#E5E5E5" strokeDasharray="2 2" />
            <XAxis dataKey="label" stroke="#5C5C5C" fontSize={11} />
            <YAxis stroke="#5C5C5C" fontSize={11} />
            <Tooltip contentStyle={CHART_TOOLTIP_STYLE} formatter={formatRevenue} />
            <Line
              type="monotone"
              dataKey="revenue"
              stroke="#002FA7"
              strokeWidth={2.5}
              dot={CHART_DOT_STYLE}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function InventoryMix({ inventoryByType }) {
  const max = Math.max(...Object.values(inventoryByType), 1);
  return (
    <div className="bg-canvas-card border border-canvas-line p-5">
      <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-ink-muted mb-1">
        02 / Inventory Mix
      </div>
      <h3 className="font-display text-lg font-semibold text-ink mb-4">Available stock</h3>
      <div className="space-y-3">
        {TYPE_META.map(({ key, label, icon: Icon }) => {
          const v = inventoryByType[key] || 0;
          return (
            <div key={key}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <Icon size={14} weight="bold" className="text-ink" />
                  <span className="text-sm font-medium text-ink">{label}</span>
                </div>
                <span className="font-mono text-sm font-semibold text-ink">{v}</span>
              </div>
              <div className="h-1.5 bg-canvas-panel">
                <div
                  className="h-full bg-ink transition-all duration-300"
                  style={{ width: `${(v / max) * 100}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TopSellers({ topModels }) {
  return (
    <div className="bg-canvas-card border border-canvas-line">
      <div className="px-5 py-4 border-b border-canvas-line flex items-center justify-between">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-ink-muted mb-1">
            03 / Top Sellers
          </div>
          <h3 className="font-display text-lg font-semibold text-ink">Best models</h3>
        </div>
      </div>
      <div className="p-5">
        {topModels.length === 0 ? (
          <p className="font-mono text-xs text-ink-subtle">No sales recorded yet.</p>
        ) : (
          <div className="h-56" style={{ minHeight: 200 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={topModels} layout="vertical">
                <CartesianGrid stroke="#E5E5E5" strokeDasharray="2 2" />
                <XAxis type="number" stroke="#5C5C5C" fontSize={11} />
                <YAxis type="category" dataKey="name" stroke="#5C5C5C" fontSize={11} width={120} />
                <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
                <Bar dataKey="units" fill="#0A0A0A" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}

function RecentSales({ sales }) {
  return (
    <div className="bg-canvas-card border border-canvas-line">
      <div className="px-5 py-4 border-b border-canvas-line flex items-center justify-between">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-ink-muted mb-1">
            04 / Activity
          </div>
          <h3 className="font-display text-lg font-semibold text-ink">Recent sales</h3>
        </div>
        <Receipt size={18} weight="bold" className="text-ink" />
      </div>
      <ul className="divide-y divide-canvas-line">
        {sales.length === 0 && (
          <li className="px-5 py-8 font-mono text-xs text-ink-subtle">No recent sales.</li>
        )}
        {sales.map((s) => {
          const vehLabel = vehicleLabel(s.vehicle) || "—";
          return (
            <li
              key={s.id}
              data-testid={`recent-sale-${s.id}`}
              className="px-5 py-3 flex items-center justify-between gap-4"
            >
              <div className="min-w-0">
                <div className="text-sm font-semibold text-ink truncate">{vehLabel}</div>
                <div className="font-mono text-[11px] text-ink-muted truncate">
                  {s.customer?.name} · {fmtDate(s.sale_date)}
                </div>
              </div>
              <div className="font-mono text-sm font-semibold text-ink shrink-0">
                {fmtMoney(s.sale_price)}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ReceivablesWidget({ data }) {
  if (!data) return null;
  const { finance = {}, exchange = {}, fy_window } = data;
  const fin_total = (finance.pending_amount || 0) + (finance.short_amount || 0);
  const ex_total = (exchange.pending_amount || 0) + (exchange.short_amount || 0);
  if (fin_total === 0 && ex_total === 0) return null;
  return (
    <div className="bg-canvas-card border border-canvas-line" data-testid="receivables-widget">
      <div className="px-5 py-4 border-b border-canvas-line flex items-center justify-between">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-ink-muted mb-1">
            05 / Outstanding YTD · {fy_window?.from} → {fy_window?.to}
          </div>
          <h3 className="font-display text-lg font-semibold text-ink">Receivables</h3>
        </div>
        <a
          href="/accounts"
          className="font-mono text-[10px] uppercase tracking-wider text-ink-muted hover:text-ink"
          data-testid="receivables-widget-link"
        >
          Manage →
        </a>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-canvas-line">
        <div className="bg-canvas-card p-4" data-testid="dash-finance-pending">
          <div className="flex items-center gap-2 mb-2">
            <Bank size={14} weight="bold" className="text-destructive" />
            <span className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">
              Finance Pending
            </span>
          </div>
          <div className="font-display text-xl font-bold text-ink tabular-nums">
            {fmtMoney(finance.pending_amount || 0)}
          </div>
          <div className="font-mono text-[10px] text-ink-subtle mt-1">
            {finance.pending_count || 0} sales
          </div>
        </div>
        <div className="bg-canvas-card p-4" data-testid="dash-finance-short">
          <div className="flex items-center gap-2 mb-2">
            <WarningCircle size={14} weight="bold" className="text-[#A35700]" />
            <span className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">
              Finance Short
            </span>
          </div>
          <div className="font-display text-xl font-bold text-ink tabular-nums">
            {fmtMoney(finance.short_amount || 0)}
          </div>
          <div className="font-mono text-[10px] text-ink-subtle mt-1">
            {finance.short_count || 0} sales
          </div>
        </div>
        <div className="bg-canvas-card p-4" data-testid="dash-exchange-pending">
          <div className="flex items-center gap-2 mb-2">
            <ArrowsLeftRight size={14} weight="bold" className="text-destructive" />
            <span className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">
              Exchange Pending
            </span>
          </div>
          <div className="font-display text-xl font-bold text-ink tabular-nums">
            {fmtMoney(exchange.pending_amount || 0)}
          </div>
          <div className="font-mono text-[10px] text-ink-subtle mt-1">
            {exchange.pending_count || 0} sales
          </div>
        </div>
        <div className="bg-canvas-card p-4" data-testid="dash-exchange-short">
          <div className="flex items-center gap-2 mb-2">
            <Clock size={14} weight="bold" className="text-[#A35700]" />
            <span className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">
              Exchange Short
            </span>
          </div>
          <div className="font-display text-xl font-bold text-ink tabular-nums">
            {fmtMoney(exchange.short_amount || 0)}
          </div>
          <div className="font-mono text-[10px] text-ink-subtle mt-1">
            {exchange.short_count || 0} sales
          </div>
        </div>
      </div>
    </div>
  );
}

function TasksWidget() {
  const [sum, setSum] = useState(null);
  useEffect(() => {
    api.get("/tasks/summary").then((r) => setSum(r.data)).catch(() => setSum(null));
  }, []);
  if (!sum) return null;
  const anyPending = (sum.overdue + sum.due_today + sum.this_week) > 0;
  if (!anyPending && (sum.upcoming || []).length === 0) return null;
  return (
    <div className="border border-canvas-line bg-canvas-card" data-testid="dashboard-tasks-widget">
      <div className="px-5 py-3 border-b border-canvas-line flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ClipboardText size={14} className="text-ink-muted" />
          <span className="text-sm font-semibold">Task Alerts</span>
        </div>
        <a href="/tasks" className="font-mono text-[10px] uppercase tracking-widest text-ink-muted hover:text-ink">View all →</a>
      </div>
      <div className="px-5 py-4 grid grid-cols-3 gap-3 border-b border-canvas-line">
        <div>
          <div className="font-mono text-[9px] uppercase tracking-widest text-destructive">Overdue</div>
          <div className="font-display text-2xl font-bold tabular-nums">{sum.overdue}</div>
        </div>
        <div>
          <div className="font-mono text-[9px] uppercase tracking-widest text-amber-600">Due Today</div>
          <div className="font-display text-2xl font-bold tabular-nums">{sum.due_today}</div>
        </div>
        <div>
          <div className="font-mono text-[9px] uppercase tracking-widest text-sky-600">This Week</div>
          <div className="font-display text-2xl font-bold tabular-nums">{sum.this_week}</div>
        </div>
      </div>
      {(sum.upcoming || []).length > 0 && (
        <ul className="divide-y divide-canvas-line">
          {sum.upcoming.slice(0, 6).map((u) => (
            <li key={u.id} className="px-5 py-2 flex items-center justify-between text-xs" data-testid={`dashboard-task-${u.id}`}>
              <div className="truncate">
                <span className="font-semibold">{u.title}</span>
                <span className="ml-2 font-mono text-[9px] uppercase tracking-widest text-ink-muted">{u.category}</span>
              </div>
              <span className="font-mono text-[10px] text-ink-muted">{u.due}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [branchFilter, setBranchFilter] = useState("");
  const [modelFilter, setModelFilter] = useState("");

  const load = useCallback(async () => {
    const params = {};
    if (branchFilter) params.branch_id = branchFilter;
    if (modelFilter) params.model_category = modelFilter;
    const { data: stats } = await api.get("/dashboard/stats", { params });
    setData(stats);
  }, [branchFilter, modelFilter]);

  useEffect(() => {
    load();
  }, [load]);

  if (!data) {
    return <div className="p-8 font-mono text-sm text-ink-muted">Loading dashboard…</div>;
  }

  return (
    <div>
      <PageHeader
        eyebrow="00 / Overview"
        title="Operations Dashboard"
        subtitle="Live snapshot of inventory health, revenue throughput, and recent sales activity."
        actions={
          <div className="flex items-center gap-3">
            <BranchFilter value={branchFilter} onChange={setBranchFilter} testId="dashboard-branch-filter" />
            <ModelCategoryFilter value={modelFilter} onChange={setModelFilter} testId="dashboard-model-filter" />
          </div>
        }
      />

      <div className="px-8 py-8 space-y-8">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Stat label="Total Revenue" value={fmtMoney(data.total_revenue)} icon={CurrencyDollar} accent="bg-brand text-white" testId="kpi-revenue" />
          <Stat label="Units Sold" value={fmtNumber(data.units_sold)} icon={TrendUp} testId="kpi-units" />
          <Stat label="Active Bookings" value={fmtNumber(data.active_bookings || 0)} icon={CalendarCheck} accent="bg-[#FFD700] text-ink" testId="kpi-bookings" />
          <Stat label="In Stock" value={fmtNumber(data.available)} icon={Package} testId="kpi-stock" />
          <Stat label="Customers" value={fmtNumber(data.total_customers)} icon={Users} testId="kpi-customers" />
        </div>

        <PendingApprovalsWidget />

        <TasksWidget />

        <LifecycleWidget />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <RevenueChart monthly={data.monthly} modelFilter={modelFilter} />
          <InventoryMix inventoryByType={data.inventory_by_type} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <SalesMixDonut rows={data.sales_by_model || []} />
          <div className="lg:col-span-2">
            <TopSellers topModels={data.top_models} />
          </div>
        </div>

        <RecentSales sales={data.recent_sales} />

        <FollowUpsWidget items={data.upcoming_followups || []} />

        <PackageUptakeWidget items={data.top_packages || []} />

        <ReceivablesWidget data={data.receivables} />
      </div>
    </div>
  );
}
